import type { FeatureId } from '../../types';

/**
 * Generates a key for a feature and trial period
 * @param featureId Feature ID being used. This will be passed in from the consuming app.
 * @param daysLeftInTrial Days left in the trial. This is fetched from our data hooks.
 * @returns A unique key for the feature and trial period
 */
export function generateFeatureKey(featureId: FeatureId, daysLeftInTrial: number): string {
	const suffix = daysLeftInTrial > 3 ? 'sot' : 'eot';
	return `${featureId}_${suffix}`;
}

/**
 * Returns whether the feature ID has been dismissed for the given trial period
 * @param featureId Feature ID being used. This will be passed in from the consuming app.
 * @param daysLeftInTrial Days left in the trial. This is fetched from our data hooks.
 * @returns Whether the feature has been dismissed for the given trial period
 */
export function isFeatureDismissedForPeriodInTrial(
	featureId: FeatureId,
	daysLeftInTrial: number,
): boolean {
	// Adhere to SSR potential issues
	if (typeof window === 'undefined') {
		return false;
	}

	const key = generateFeatureKey(featureId, daysLeftInTrial);
	return window.localStorage.getItem(key) !== null;
}

/**
 * Writes that the feature and trial period has been dismissed
 * @param featureId Feature ID being used. This will be passed in from the consuming app.
 * @param daysLeftInTrial Days left in the trial. This is fetched from our data hooks.
 */
export function markFeatureAsDismissedForTrialPeriod(
	featureId: FeatureId,
	daysLeftInTrial: number,
): void {
	// Adhere to SSR potential issues
	if (typeof window === 'undefined') {
		return;
	}

	const key = generateFeatureKey(featureId, daysLeftInTrial);
	window.localStorage.setItem(key, 'true');
}
